import {
  Facebook,
  LinkedIn,
} from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import {
  Box,
  Container,
  Grid,
  IconButton,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import React from "react";
import { HashLink } from "react-router-hash-link";
import AppLinks from "./AppLinks";

const footerMenu = [
  {
    title: "Product",
    links: [
      { to: "/#Why_Choose", label: "Why Choose Us" },
      { to: "/#Reviews", label: "Reviews" },
      { to: "/pricing", label: "Pricing" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about#what_we_offer", label: "What We Offer" },
      { to: "/about#faq", label: "FAQ" },
      { to: "/contact#contact_us", label: "Contact Us" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms & Conditions" },
      { to: "/contact#contact_us", label: "Support" },
    ],
  },
];

const socialLinks = [
  { Icon: XIcon, url: "https://x.com/MSchoollin95170", label: "Twitter" },
  { Icon: Facebook, url: "https://www.facebook.com/profile.php?id=61576745007909", label: "Facebook" },
  { Icon: LinkedIn, url: "https://myschoolling.com", label: "LinkedIn" },
];

const SocialButton = ({ Icon, url, label }) => (
  <IconButton
    component="a"
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    sx={{
      width: 40,
      height: 40,
      borderRadius: "12px",
      bgcolor: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.08)",
      color: "#64748b",
      transition: "all 0.25s ease",
      "&:hover": {
        bgcolor: "rgba(79,110,247,0.25)",
        borderColor: "rgba(79,110,247,0.4)",
        color: "#a5b4fc",
        transform: "translateY(-3px)",
      },
    }}
  >
    <Icon sx={{ fontSize: 18 }} />
  </IconButton>
);

const Footer = () => {
  return (
    <Box
      sx={{
        position: "relative",
        pt: 10,
        pb: 5,
        background: "linear-gradient(180deg, #080E24 0%, #0A1029 100%)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        overflow: "hidden",
      }}
    >
      {/* Background grid pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(79,110,247,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(79,110,247,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* Glow top-right */}
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -100,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(79,110,247,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6}>
          {/* BRAND COLUMN */}
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <img src="/logoWhite.png" alt="MySchoolling" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </Box>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{
                  background: "linear-gradient(135deg, #fff 0%, #a5b4fc 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                MySchoolling
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{ color: "#64748b", maxWidth: 320, lineHeight: 1.8, mb: 4 }}
            >
              Empowering schools and parents with intelligent learning
              tools, real-time insights, and seamless digital management
              — built for modern education.
            </Typography>

            <Stack direction="row" spacing={1} mb={5}>
              {socialLinks.map((s) => (
                <SocialButton key={s.label} Icon={s.Icon} url={s.url} label={s.label} />
              ))}
            </Stack>

            <AppLinks />
          </Grid>

          {/* MENU COLUMNS */}
          {footerMenu.map((section) => (
            <Grid item xs={6} sm={4} md={2.5} key={section.title}>
              <Typography
                variant="body2"
                fontWeight={700}
                color="#fff"
                gutterBottom
                sx={{ mb: 2.5, fontSize: "0.82rem", letterSpacing: "0.08em", textTransform: "uppercase" }}
              >
                {section.title}
              </Typography>

              <Stack spacing={1.5}>
                {section.links.map((link) => (
                  <HashLink
                    key={link.label}
                    smooth
                    to={link.to}
                    style={{ textDecoration: "none" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        fontSize: "0.9rem",
                        transition: "color 0.2s ease",
                        "&:hover": { color: "#a5b4fc" },
                      }}
                    >
                      {link.label}
                    </Typography>
                  </HashLink>
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 6, borderColor: "rgba(255,255,255,0.06)" }} />

        {/* BOTTOM BAR */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ xs: "column", md: "row" }}
          gap={2}
        >
          <Typography
            variant="body2"
            sx={{ color: "#475569", textAlign: { xs: "center", md: "left" }, fontSize: "0.85rem" }}
          >
            © {new Date().getFullYear()}{" "}
            <Box component="span" sx={{ color: "#94a3b8", fontWeight: 600 }}>
              MySchoolling™
            </Box>{" "}
            — A product of Versynix Global Pvt. Ltd. All rights reserved.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#334155",
              fontWeight: 600,
              fontSize: "0.85rem",
              textAlign: { xs: "center", md: "right" },
              background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Built for Modern Education. ✦
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
